import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuickAccess } from "../../contexts/QuickAccessContext";
import { usePermissions } from "../../contexts/PermissionsContext";
import { useSwipe } from "../../hooks/useSwipe";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SwipeNavigator = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { quickAccessPages } = useQuickAccess();
  const { hasPermission } = usePermissions();
  const [showIndicator, setShowIndicator] = useState(null);
  const [pageTransition, setPageTransition] = useState(null);

  const [swipeEnabled, setSwipeEnabled] = useState(() => {
    return localStorage.getItem("setting_swipe_enabled") === "true";
  });

  // Listen for settings changes
  useEffect(() => {
    const handleSettingsChange = () => {
      const stored = localStorage.getItem("setting_swipe_enabled");
      setSwipeEnabled(stored === null ? true : stored === "true");
    };
    window.addEventListener("settings-changed", handleSettingsChange);
    return () => window.removeEventListener("settings-changed", handleSettingsChange);
  }, []);

  // Filter pages based on permissions
  const visiblePages = quickAccessPages.filter(
    (page) => !page.permission || hasPermission(page.permission)
  );

  // Find current page index
  const getCurrentPageIndex = useCallback(() => {
    return visiblePages.findIndex(
      (page) =>
        location.pathname === page.href ||
        (page.href !== "/" && location.pathname.startsWith(page.href))
    );
  }, [location.pathname, visiblePages]);

  const currentIndex = getCurrentPageIndex();

  // Get previous and next pages
  const prevPage = currentIndex > 0 ? visiblePages[currentIndex - 1] : null;
  const nextPage =
    currentIndex < visiblePages.length - 1 && currentIndex >= 0
      ? visiblePages[currentIndex + 1]
      : null;

  const handleSwipeLeft = useCallback(() => {
    if (nextPage) {
      setPageTransition("slide-left");
      setShowIndicator(null);
      setTimeout(() => {
        navigate(nextPage.href);
      }, 50);
    }
  }, [nextPage, navigate]);

  const handleSwipeRight = useCallback(() => {
    if (prevPage) {
      setPageTransition("slide-right");
      setShowIndicator(null);
      setTimeout(() => {
        navigate(prevPage.href);
      }, 50);
    }
  }, [prevPage, navigate]);

  const { handlers, isSwiping, swipeDirection, swipeProgress } = useSwipe({
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
    threshold: 100, // Minimum swipe distance to trigger navigation
    requireEdgeStart: false, // Allow swiping from anywhere on the page
    excludeSelector: "[data-swipe-exclude]", // Exclude elements with this attribute (like dashboard selector)
    enabled: swipeEnabled && visiblePages.length > 1,
  });

  // Show edge indicator while swiping
  useEffect(() => {
    if (isSwiping && swipeProgress > 20) {
      if (swipeDirection === "right" && prevPage) {
        setShowIndicator("left");
      } else if (swipeDirection === "left" && nextPage) {
        setShowIndicator("right");
      }
    } else {
      setShowIndicator(null);
    }
  }, [isSwiping, swipeDirection, swipeProgress, prevPage, nextPage]);

  // Reset transition after navigation
  useEffect(() => {
    if (pageTransition) {
      const timer = setTimeout(() => {
        setPageTransition(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, pageTransition]);

  // Only show on mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;

  if (!isMobile) {
    return <>{children}</>;
  }

  return (
    <div
      className="relative min-h-full"
      {...handlers}
      style={{ touchAction: "pan-y" }}
    >
      {/* Left Edge Indicator */}
      {showIndicator === "left" && prevPage && (
        <div
          className="fixed left-0 top-1/2 -translate-y-1/2 z-50 flex items-center gap-1 bg-primary/90 text-primary-foreground px-3 py-2 rounded-r-lg shadow-lg transition-all duration-200 animate-slide-in-left"
          style={{
            opacity: Math.min(swipeProgress / 50, 1),
          }}
        >
          <ChevronLeft className="w-4 h-4" />
          <div className="flex flex-col">
            <span className="text-[10px] opacity-75">Swipe to</span>
            <span className="text-xs font-medium">{prevPage.name}</span>
          </div>
        </div>
      )}

      {/* Right Edge Indicator */}
      {showIndicator === "right" && nextPage && (
        <div
          className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex items-center gap-1 bg-primary/90 text-primary-foreground px-3 py-2 rounded-l-lg shadow-lg transition-all duration-200 animate-slide-in-right"
          style={{
            opacity: Math.min(swipeProgress / 50, 1),
          }}
        >
          <div className="flex flex-col text-right">
            <span className="text-[10px] opacity-75">Swipe to</span>
            <span className="text-xs font-medium">{nextPage.name}</span>
          </div>
          <ChevronRight className="w-4 h-4" />
        </div>
      )}

      {/* Page Content with Transition */}
      <div
        className={`transition-transform duration-200 ease-out ${
          pageTransition === "slide-left"
            ? "animate-page-slide-left"
            : pageTransition === "slide-right"
            ? "animate-page-slide-right"
            : ""
        }`}
      >
        {children}
      </div>

      {/* Swipe Progress Indicator (subtle line at bottom) */}
      {isSwiping && swipeProgress > 10 && (
        <div className="fixed bottom-16 left-0 right-0 h-1 bg-muted z-40 lg:hidden">
          <div
            className={`h-full bg-primary/50 transition-all duration-100 ${
              swipeDirection === "right" ? "ml-0" : "ml-auto"
            }`}
            style={{
              width: `${swipeProgress}%`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SwipeNavigator;
